import { useEffect, useState } from "react";

/**
 * Whether the browser currently has a network connection.
 *
 * Event-driven rather than polled: the browser fires `online`/`offline` the
 * moment the OS reports the interface up or down, so a dropped Wi-Fi
 * connection surfaces immediately and costs no requests. A poll would be both
 * slower to notice and pointless traffic on a connection that is already down.
 *
 * Known limit — `navigator.onLine` answers "is there a network interface",
 * not "can I reach the internet". A laptop on a captive-portal Wi-Fi, or a
 * router with no upstream, still reports `true`. Those cases surface as failed
 * requests instead; `getApiResponseErrorMsg` words a response-less failure as
 * a connection problem rather than a server fault, which is the honest thing
 * to say when we genuinely cannot tell the two apart from the client.
 */
export const useOnlineStatus = (): boolean => {
  // `navigator` is guarded for the happy-dom/SSR case where it may be absent —
  // an app that cannot tell should assume online rather than accuse the user
  // of being offline.
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Re-sync on mount. The events only fire on a transition, so a tab that
    // was opened (or hot-reloaded) while already offline would otherwise sit
    // on the optimistic initial value and never be corrected.
    if (typeof navigator !== "undefined") {
      setOnline(navigator.onLine);
    }

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return online;
};

export default useOnlineStatus;
